/**
 * The funnel schema — PRD §3.2, "the most important technical decision in the
 * project".
 *
 * RULE-014: "design the schema explicitly and first. Do not let it emerge from
 * the UI. It is validated by a single Zod definition serving the builder, the
 * runtime, and the JSON schema passed to the LLM for structured output."
 * This file is that definition. There is no second validator.
 *
 * RULE-012 is the shape's other constraint: capture is modality-agnostic.
 * Nothing here says "form". A block is a place an answer comes from; a
 * transcript at M5 fills the same rubric through `source: 'transcript'`
 * without a new field.
 *
 * DECISION-014: **no node coordinate and no lane field.** M1's Build tab is
 * the linear step list; the flow canvas is M6, and its geometry does not enter
 * the funnel schema now to be migrated later.
 * `test/no-canvas-geometry.test.ts` enforces that against this file's source.
 */
import { z } from 'zod'
import { archetypeSchema } from './archetype'
import { conditionSchema, rubricSchema } from './rubric'

/** PRD §3.2: "funnel schema JSON must stay well under" D1's 2 MB row cap.
 *  512 KB is the "well under" — a quarter of the cap, and roughly a hundred
 *  times the largest realistic funnel. */
export const MAX_FUNNEL_BYTES = 512 * 1024

/* ── blocks ─────────────────────────────────────────────────────────────── */

/**
 * The 17 MVP block types.
 *
 * `design/screen-manifest.md` records the mockup's palette as 24 types;
 * DECISION-021 cuts seven — image select, image, video, ranking, matrix,
 * slider, file upload — "leaving 17", and `docs/BACKLOG.md` carries them.
 * 24 − 7 = 17 is the arithmetic the enum below satisfies.
 *
 * Two readings of Story 2.1's parenthetical list are settled here, both by
 * DECISION-021's count. "Card select single/multi" is ONE type carrying a
 * `selection` mode — the `editor-build` inspector renders it as the radio pair
 * *Exactly one answer* / *One or more answers*, not as two palette entries.
 * "Contact block" is the palette's GROUP HEADING over Email · Phone · Name,
 * not a block. Counting either the other way gives 18 or 19, and the mockup's
 * palette contains neither.
 */
export const BLOCK_TYPES = [
  // Choice
  'card_select',
  'dropdown',
  // Text
  'short_text',
  'long_text',
  // Contact
  'email',
  'phone',
  'name',
  // Number
  'number',
  'currency',
  'scale',
  // Date
  'date',
  // Advanced
  'hidden_field',
  'consent',
  // Content
  'intro',
  'divider',
  'rich_text',
  'outcome',
] as const

export const blockTypeSchema = z.enum(BLOCK_TYPES)
export type BlockType = z.infer<typeof blockTypeSchema>

/** Blocks that produce an answer. The rest are content. */
export const QUESTION_BLOCK_TYPES = [
  'card_select',
  'dropdown',
  'short_text',
  'long_text',
  'email',
  'phone',
  'name',
  'number',
  'currency',
  'scale',
  'date',
  'hidden_field',
  'consent',
] as const satisfies readonly BlockType[]

export const OPTION_BLOCK_TYPES = ['card_select', 'dropdown'] as const satisfies readonly BlockType[]
export const NUMERIC_BLOCK_TYPES = ['number', 'currency', 'scale'] as const satisfies readonly BlockType[]
export const TEXT_BLOCK_TYPES = ['short_text', 'long_text'] as const satisfies readonly BlockType[]

/** PRD §3.6. Adding a long-text field does not automatically hand it to the
 *  AI; the operator chooses, and the default is the one that spends nothing. */
export const FREE_TEXT_MODES = ['capture_only', 'ai_extraction', 'rules'] as const
export const freeTextModeSchema = z.enum(FREE_TEXT_MODES)
export type FreeTextMode = z.infer<typeof freeTextModeSchema>

const id = z.string().min(1).max(64)
const fieldKey = z
  .string()
  .regex(/^[a-z][a-z0-9_]{0,63}$/, 'lower_snake_case, starting with a letter')

/** A step, block, rule or action id: local to one funnel document. Exported for
 *  `operations.ts` (Story 27.1), whose arguments name the same things. */
export const localIdSchema = id
/** A block's `field_key`, as the funnel holds it. */
export const fieldKeySchema = fieldKey

/**
 * An option's id is TWO things, and DECISION-067 is what makes them one rule:
 * it is the KEY in the rubric's `points_by_option` record, and it is the
 * ANSWER a respondent submits when they choose that option. `optionAnswer` in
 * `scoring.ts` is the single function that says so, and the embed, the
 * calibration sampler and `crossReference` all read it — see `value` below for
 * what used to happen when they disagreed.
 *
 * Because it is a record key, `__proto__` has to be refused here. Zod silently
 * drops it rather than letting it reach `Object.prototype`, which is the right
 * behaviour for safety and the wrong behaviour for an operator who names an
 * option `__proto__` and assigns it 25 points: the key vanishes, the
 * cross-reference check never sees it, and the criterion awards zero for that
 * answer forever with nothing reported. Refuse the id instead, once, where it
 * is created.
 */
const optionId = id.refine((value) => value !== '__proto__', {
  message: '`__proto__` cannot be an option id — it would be dropped from the rubric points map and the option would silently score zero',
})
/** An option's id, with its `__proto__` refusal. */
export const optionIdSchema = optionId

export const optionSchema = z.object({
  id: optionId,
  label: z.string().min(1).max(300),
  /**
   * EXPORT METADATA. Never the answer, never a scoring key — DECISION-067.
   *
   * This field used to claim it was "what lands in the answer when absent — the
   * id", and `packages/embed/src/blocks.tsx` implemented that as
   * `option.value ?? option.id` while the rubric, `crossReference` and
   * `calibrate.ts` all keyed on `id`. An option carrying an explicit `value`
   * was therefore submitted as `value`, matched nothing in
   * `points_by_option`, and fell through to `default_points` — scoring zero
   * with nothing reported. Q-044 found it; DECISION-067 settled it by making
   * the id the answer in every layer at once.
   *
   * Its old rationale — "lets an operator rename a label without invalidating
   * the rubric" — was redundant even then: keying on ids already delivers
   * exactly that, because `label` is a separate field regardless.
   *
   * What it is FOR now is the code a downstream system expects — a CRM's
   * picklist key, a spreadsheet column value — carried to exports,
   * notifications, dispatch and writeback by `lib/serialize.ts` and resolved
   * nowhere else (RULE-050, DECISION-013).
   */
  value: z.string().max(300).optional(),
  description: z.string().max(500).optional(),
})
export type BlockOption = z.infer<typeof optionSchema>

const questionBase = {
  id,
  field_key: fieldKey,
  question: z.string().min(1).max(500),
  help_text: z.string().max(1000).optional(),
  required: z.boolean().default(false),
  /** Which `leads` contact column this answer fills. Absent means none — a
   *  `short_text` asking for a company name is the only way `contact_company`
   *  is ever reachable, since the 17 types have no company block. */
  maps_to: z.enum(['name', 'email', 'phone', 'company']).optional(),
}

const contentBase = { id }

export const blockSchema = z.discriminatedUnion('type', [
  z.object({
    ...questionBase,
    type: z.literal('card_select'),
    selection: z.enum(['single', 'multi']).default('single'),
    options: z.array(optionSchema).min(1).max(50),
    min_select: z.number().int().min(0).optional(),
    max_select: z.number().int().min(1).optional(),
  }),
  z.object({
    ...questionBase,
    type: z.literal('dropdown'),
    options: z.array(optionSchema).min(1).max(200),
    placeholder: z.string().max(200).optional(),
  }),
  z.object({
    ...questionBase,
    type: z.literal('short_text'),
    placeholder: z.string().max(200).optional(),
    max_length: z.number().int().min(1).max(2000).default(200),
    free_text_mode: freeTextModeSchema.default('capture_only'),
  }),
  z.object({
    ...questionBase,
    type: z.literal('long_text'),
    placeholder: z.string().max(200).optional(),
    max_length: z.number().int().min(1).max(20000).default(5000),
    free_text_mode: freeTextModeSchema.default('capture_only'),
  }),
  z.object({ ...questionBase, type: z.literal('email'), placeholder: z.string().max(200).optional() }),
  z.object({ ...questionBase, type: z.literal('phone'), placeholder: z.string().max(200).optional() }),
  z.object({ ...questionBase, type: z.literal('name'), placeholder: z.string().max(200).optional() }),
  z.object({
    ...questionBase,
    type: z.literal('number'),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().positive().optional(),
    unit: z.string().max(20).optional(),
  }),
  z.object({
    ...questionBase,
    type: z.literal('currency'),
    /** ISO 4217. The answer value is INTEGER CENTS (DECISION-008) — never a
     *  float, anywhere, including here. */
    currency_code: z.string().regex(/^[A-Z]{3}$/, 'an ISO 4217 code, e.g. USD'),
    min_cents: z.number().int().min(0).optional(),
    max_cents: z.number().int().min(0).optional(),
  }),
  z.object({
    ...questionBase,
    type: z.literal('scale'),
    min: z.number().int(),
    max: z.number().int(),
    min_label: z.string().max(100).optional(),
    max_label: z.string().max(100).optional(),
  }),
  z.object({
    ...questionBase,
    type: z.literal('date'),
    /** ISO 8601 dates, inclusive. */
    min: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
    max: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  }),
  z.object({
    id,
    type: z.literal('hidden_field'),
    field_key: fieldKey,
    /** Where the value comes from: a URL parameter on the embed, a constant,
     *  or a `set_hidden_field` logic action. */
    source: z.enum(['url_param', 'static', 'logic']).default('static'),
    url_param: z.string().max(100).optional(),
    default_value: z.string().max(500).optional(),
  }),
  z.object({
    ...questionBase,
    type: z.literal('consent'),
    statement: z.string().min(1).max(2000),
    link_url: z.string().url().optional(),
    link_label: z.string().max(200).optional(),
  }),
  z.object({
    ...contentBase,
    type: z.literal('intro'),
    heading: z.string().min(1).max(300),
    body: z.string().max(4000).optional(),
  }),
  z.object({ ...contentBase, type: z.literal('divider') }),
  z.object({ ...contentBase, type: z.literal('rich_text'), body: z.string().min(1).max(10000) }),
  /** Placement marker for the outcome content. What it renders lives in
   *  `outcomes[]`, keyed by band — one funnel, one outcome per band. */
  z.object({ ...contentBase, type: z.literal('outcome') }),
])
export type Block = z.infer<typeof blockSchema>

/**
 * Can an extraction field read this block?
 *
 * PRD §3.6: free text reaches the AI only when the operator put the question in
 * `ai_extraction` mode — "adding a long-text field does not automatically hand
 * it to the AI". `crossReference` below refuses an extraction field on anything
 * else, and Story 20.2's source select offers nothing else, so the two ask ONE
 * question here instead of each keeping its own copy of it.
 * `test/extraction-source.test.ts` holds them to the same answer for every
 * free-text type in every mode.
 */
export function acceptsExtraction(block: Block): boolean {
  return (
    (block.type === 'short_text' || block.type === 'long_text') &&
    block.free_text_mode === 'ai_extraction'
  )
}
export type BlockInput = z.input<typeof blockSchema>

/* ── steps ──────────────────────────────────────────────────────────────── */

export const LAYOUT_MODES = ['wizard', 'grouped', 'single_page', 'conversational'] as const
export const layoutModeSchema = z.enum(LAYOUT_MODES)
export type LayoutMode = z.infer<typeof layoutModeSchema>

export const PROGRESS_STYLES = ['bar', 'steps', 'none'] as const
export const progressStyleSchema = z.enum(PROGRESS_STYLES)
export type ProgressStyle = z.infer<typeof progressStyleSchema>

/**
 * PRD §3.2, as amended by DECISION-030: "steps[] ordered; each has blocks[]",
 * alongside a top-level "blocks[] questions and non-question content".
 *
 * Read as: `blocks[]` is the flat registry — one place a `field_key` is
 * unique, one place `logic[]` and the rubric resolve an id — and a step's
 * `blocks` is the ordered list of ids it shows. Both PRD field names survive;
 * neither requires a tree walk to answer "does this id exist".
 *
 * A step has **no layout of its own** (DECISION-030). The diagram's third word
 * had no vocabulary behind it: the only layout the product has is
 * `settings.layout_mode`, which `editor-design` presents as one choice for the
 * whole funnel, and a per-step override means almost nothing under `wizard`
 * where a step is already one screen. PRD §3.2's shape line is amended to
 * match. Adding one later is an optional field and a no-op migration rung.
 */
export const stepSchema = z.object({
  id,
  title: z.string().max(200).optional(),
  blocks: z.array(id).max(200),
})
export type Step = z.infer<typeof stepSchema>

/* ── logic ──────────────────────────────────────────────────────────────── */

export const LOGIC_ACTIONS = ['show', 'hide', 'skip_to', 'disqualify', 'set_hidden_field'] as const
export const logicActionSchema = z.enum(LOGIC_ACTIONS)
export type LogicAction = z.infer<typeof logicActionSchema>

export const logicEffectSchema = z.discriminatedUnion('action', [
  z.object({ action: z.literal('show'), target_block_id: id }),
  z.object({ action: z.literal('hide'), target_block_id: id }),
  z.object({ action: z.literal('skip_to'), target_step_id: id }),
  z.object({ action: z.literal('disqualify'), reason: z.string().max(500).optional() }),
  z.object({
    action: z.literal('set_hidden_field'),
    target_block_id: id,
    value: z.string().max(500),
  }),
])

/**
 * `editor-logic` states the evaluation contract in the screen itself:
 * **"Rules run in order. The first matching rule wins."** The array order IS
 * that order, and DECISION-034 fixes the scope the screen leaves open: first
 * match **within each step's group**, with the winners across steps all
 * applying. `logic.ts` is the one implementation of it — the runtime (Story
 * 5.2), the Logic tab (6.4) and the calibration sampler (2.4) read from there
 * rather than each walking this array their own way.
 */
export const logicRuleSchema = z.object({
  id,
  /** The step this rule attaches to. Not decoration: it is the grouping
   *  `editor-logic` renders AND the group DECISION-034 evaluates within. A
   *  rule without one is its own group. */
  step_id: id.optional(),
  when: conditionSchema,
  then: logicEffectSchema,
})
export type LogicRule = z.infer<typeof logicRuleSchema>

/* ── settings, theme, outcomes, voice, routing ──────────────────────────── */

export const settingsSchema = z.object({
  layout_mode: layoutModeSchema.default('wizard'),
  progress_style: progressStyleSchema.default('bar'),
  /** The resume capability URL, `GET /public/r/{token}`. */
  resume: z
    .object({ enabled: z.boolean().default(false), ttl_hours: z.number().int().min(1).max(720).default(72) })
    .default({}),
  /** A `complete:false` submission writes a `partial` lead — unmetered. */
  partial_capture: z.object({ enabled: z.boolean().default(true) }).default({}),
})

/** PRD §3.2 `theme_ref`: "id of applied theme (or inline override)", plus
 *  §3.7's `inherit` mode, which reads the host page's font stack and accent
 *  (manifest B15: which field stores the choice was unspecified — this one). */
export const themeRefSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('preset'), preset_id: id }),
  z.object({ kind: z.literal('inherit') }),
  /** Preset CONTENT, not Leadgible tokens — see DESIGN-SYSTEM.md §4.7. A theme's
   *  own palette is data an operator authors, not a value this codebase
   *  hardcodes. */
  z.object({ kind: z.literal('inline'), tokens: z.record(z.string().min(1), z.string().max(200)) }),
])
export type ThemeRef = z.infer<typeof themeRefSchema>

export const outcomeSchema = z.object({
  band_id: id,
  heading: z.string().min(1).max(300),
  body: z.string().max(4000).default(''),
  cta: z.object({ label: z.string().min(1).max(200), url: z.string().url() }).optional(),
  redirect_url: z.string().url().optional(),
})
export type Outcome = z.infer<typeof outcomeSchema>

/** M5 (PRD §3.14). Typed now because RULE-014 designs the schema first and
 *  because a persona is per band — a shape the rubric has to support today. */
export const voiceSchema = z.object({
  enabled: z.boolean().default(false),
  personas: z
    .array(
      z.object({
        id,
        band_id: id,
        name: z.string().min(1).max(200),
        instructions: z.string().max(8000),
      }),
    )
    .max(10)
    .default([]),
  knowledge_refs: z.array(id).max(50).default([]),
  caps: z
    .object({
      max_duration_s: z.number().int().min(30).max(3600).default(600),
      max_calls_per_lead: z.number().int().min(1).max(10).default(1),
    })
    .default({}),
  /** PRD §5.8: recording and transcript consent, captured BEFORE session start. */
  disclosure: z.string().max(2000).default(''),
})

export const ACTION_KINDS = ['email', 'slack', 'webhook', 'ghl', 'voice'] as const
export const CADENCES = ['instant', 'hourly', 'daily', 'weekly'] as const

/** Named here so `db/schema.ts` and `routing.ts` cannot each invent their own —
 *  `tests/routing-vocabulary.test.ts` holds the two enums to this one (9.1). */
export type ActionKind = (typeof ACTION_KINDS)[number]
export type Cadence = (typeof CADENCES)[number]

/** PRD §3.10: cadence is a property of the ACTION, never the recipient and
 *  never the funnel. `notification_actions` is the derived projection of this
 *  (DECISION-007), rewritten on every publish. */
export const routingActionSchema = z.object({
  id,
  kind: z.enum(ACTION_KINDS),
  recipients: z.array(z.string().min(1).max(320)).max(50).default([]),
  cadence: z.enum(CADENCES).default('instant'),
  cadence_at: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).optional(),
  cadence_day: z.number().int().min(0).max(6).optional(),
  /** Webhook URL, Slack channel, GHL pipeline — kind-specific, opaque here.
   *  The credential itself never appears in a funnel (RULE-049). */
  config: z.record(z.string().min(1), z.string().max(2000)).default({}),
  enabled: z.boolean().default(true),
})

export type RoutingAction = z.infer<typeof routingActionSchema>

export const routingRuleSchema = z.object({
  band_id: id,
  actions: z.array(routingActionSchema).max(20).default([]),
})
export type RoutingRule = z.infer<typeof routingRuleSchema>

/* ── meta ───────────────────────────────────────────────────────────────── */

export const metaSchema = z.object({
  id: z.string().regex(/^fnl_[A-Za-z0-9_-]{8,}$/, 'an opaque `fnl_` id'),
  org_id: z.string().min(1),
  workspace_id: z.string().regex(/^ws_[A-Za-z0-9_-]{8,}$/, 'an opaque `ws_` id'),
  name: z.string().min(1).max(200),
  /** The funnel's OWN version counter — `funnel_versions.version`. Distinct
   *  from the top-level `schema_version`, which is the shape's version and the
   *  only thing `migrate.ts` reads. */
  version: z.number().int().min(1).default(1),
  archetype: archetypeSchema,
  /** PRD §3.3: presets are snapshots. Recording which one it came from never
   *  makes it a live link. */
  created_from_preset: z.object({ id, version: z.number().int().min(1) }).optional(),
})

/* ── the funnel ─────────────────────────────────────────────────────────── */

const funnelShape = z.object({
  /** The SHAPE's version. `migrate.ts` reads this and nothing else. */
  schema_version: z.number().int().min(1),
  meta: metaSchema,
  settings: settingsSchema.default({}),
  theme_ref: themeRefSchema.default({ kind: 'inherit' }),
  steps: z.array(stepSchema).max(100),
  blocks: z.array(blockSchema).max(500),
  logic: z.array(logicRuleSchema).max(200).default([]),
  rubric: rubricSchema,
  outcomes: z.array(outcomeSchema).max(10).default([]),
  voice: voiceSchema.default({}),
  routing: z.array(routingRuleSchema).max(10).default([]),
})

export const funnelSchema = funnelShape.superRefine(crossReference)
export type Funnel = z.infer<typeof funnelSchema>
export type FunnelInput = z.input<typeof funnelSchema>

type FunnelShape = z.infer<typeof funnelShape>

function crossReference(funnel: FunnelShape, ctx: z.RefinementCtx): void {
  const issue = (path: (string | number)[], message: string) =>
    ctx.addIssue({ code: z.ZodIssueCode.custom, path, message })

  const blocksById = new Map<string, Block>()
  funnel.blocks.forEach((block, i) => {
    if (blocksById.has(block.id)) {
      issue(['blocks', i, 'id'], `two blocks share the id "${block.id}" — ids must be unique`)
    }
    blocksById.set(block.id, block)
  })

  // Bounds that contradict themselves. A block whose minimum exceeds its
  // maximum accepts no answer at all, which the runtime discovers as an
  // unanswerable question and the calibration bench as an empty distribution.
  funnel.blocks.forEach((block, i) => {
    const bound = (
      low: number | undefined,
      high: number | undefined,
      lowField: string,
      highField: string,
    ) => {
      if (low !== undefined && high !== undefined && low > high) {
        issue(
          ['blocks', i, lowField],
          `${lowField} (${low}) is above ${highField} (${high}) — no answer can satisfy both`,
        )
      }
    }
    switch (block.type) {
      case 'number':
        bound(block.min, block.max, 'min', 'max')
        break
      case 'scale':
        bound(block.min, block.max, 'min', 'max')
        break
      case 'currency':
        bound(block.min_cents, block.max_cents, 'min_cents', 'max_cents')
        break
      case 'date':
        if (block.min !== undefined && block.max !== undefined && block.min > block.max) {
          issue(['blocks', i, 'min'], `min (${block.min}) is after max (${block.max}) — no date can satisfy both`)
        }
        break
      case 'card_select': {
        bound(block.min_select, block.max_select, 'min_select', 'max_select')
        const count = block.options.length
        if (block.min_select !== undefined && block.min_select > count) {
          issue(
            ['blocks', i, 'min_select'],
            `min_select is ${block.min_select} but the block offers ${count} options — nobody can answer it`,
          )
        }
        if (block.selection === 'single' && block.max_select !== undefined && block.max_select > 1) {
          issue(
            ['blocks', i, 'max_select'],
            'a single-selection card select cannot take more than one answer — set selection to multi, or drop max_select',
          )
        }
        break
      }
      default:
        break
    }
  })

  // PRD §3.10: cadence is a property of the ACTION. DECISION-007 projects
  // these rows into `notification_actions` on every publish, and the digest
  // scheduler queries them by (cadence, cadence_at) — so a daily action with
  // no time is a row the scheduler will never select and nobody will notice.
  funnel.routing.forEach((rule, i) => {
    rule.actions.forEach((action, j) => {
      const path = (field: string) => ['routing', i, 'actions', j, field]
      const needsTime = action.cadence === 'daily' || action.cadence === 'weekly'
      if (needsTime && action.cadence_at === undefined) {
        issue(path('cadence_at'), `a ${action.cadence} action needs a time — the digest scheduler selects on it`)
      }
      if (!needsTime && action.cadence_at !== undefined) {
        issue(path('cadence_at'), `an ${action.cadence} action fires on arrival, so a time means nothing here`)
      }
      if (action.cadence === 'weekly' && action.cadence_day === undefined) {
        issue(path('cadence_day'), 'a weekly action needs a day of the week (0 = Sunday)')
      }
      if (action.cadence !== 'weekly' && action.cadence_day !== undefined) {
        issue(path('cadence_day'), `a ${action.cadence} action does not fire on one day of the week`)
      }
    })
  })

  const keyOwner = new Map<string, number>()
  funnel.blocks.forEach((block, i) => {
    if (!('field_key' in block)) return
    const first = keyOwner.get(block.field_key)
    if (first !== undefined) {
      issue(
        ['blocks', i, 'field_key'],
        `field_key "${block.field_key}" is already used by block ${funnel.blocks[first]!.id} — a field key names one answer`,
      )
    } else {
      keyOwner.set(block.field_key, i)
    }
  })

  // Steps: every referenced block exists, and every block sits in exactly one
  // step. An orphan block is invisible and a duplicated one renders twice.
  const stepIds = new Set<string>()
  const placement = new Map<string, string>()
  funnel.steps.forEach((step, i) => {
    if (stepIds.has(step.id)) {
      issue(['steps', i, 'id'], `two steps share the id "${step.id}" — ids must be unique`)
    }
    stepIds.add(step.id)
    step.blocks.forEach((blockId, j) => {
      if (!blocksById.has(blockId)) {
        issue(['steps', i, 'blocks', j], `no block with id "${blockId}"`)
        return
      }
      const already = placement.get(blockId)
      if (already !== undefined) {
        issue(
          ['steps', i, 'blocks', j],
          `block "${blockId}" is already on step "${already}" — a block belongs to one step`,
        )
      } else {
        placement.set(blockId, step.id)
      }
    })
  })
  funnel.blocks.forEach((block, i) => {
    if (!placement.has(block.id)) {
      issue(['blocks', i, 'id'], `block "${block.id}" is on no step, so nobody ever sees it`)
    }
  })

  const extractionKeys = new Set(funnel.rubric.extraction.map((f) => f.field_key))

  const checkCondition = (
    condition: { subject: z.infer<typeof conditionSchema>['subject'] },
    path: (string | number)[],
  ) => {
    const { subject } = condition
    if (subject.kind === 'answer' && !blocksById.has(subject.block_id)) {
      issue([...path, 'subject', 'block_id'], `no block with id "${subject.block_id}"`)
    }
    if (subject.kind === 'extracted' && !extractionKeys.has(subject.field_key)) {
      issue(
        [...path, 'subject', 'field_key'],
        `no extraction field named "${subject.field_key}" — define it under rubric.extraction`,
      )
    }
  }

  // Logic: conditions read something that exists, effects target something
  // that exists, and `set_hidden_field` targets a hidden field.
  funnel.logic.forEach((rule, i) => {
    checkCondition(rule.when, ['logic', i, 'when'])
    if (rule.step_id !== undefined && !stepIds.has(rule.step_id)) {
      issue(['logic', i, 'step_id'], `no step with id "${rule.step_id}"`)
    }
    const effect = rule.then
    switch (effect.action) {
      case 'show':
      case 'hide':
        if (!blocksById.has(effect.target_block_id)) {
          issue(['logic', i, 'then', 'target_block_id'], `no block with id "${effect.target_block_id}"`)
        }
        break
      case 'skip_to':
        if (!stepIds.has(effect.target_step_id)) {
          issue(['logic', i, 'then', 'target_step_id'], `no step with id "${effect.target_step_id}"`)
        }
        break
      case 'set_hidden_field': {
        const target = blocksById.get(effect.target_block_id)
        if (target === undefined) {
          issue(['logic', i, 'then', 'target_block_id'], `no block with id "${effect.target_block_id}"`)
        } else if (target.type !== 'hidden_field') {
          issue(
            ['logic', i, 'then', 'target_block_id'],
            `"${target.id}" is a ${target.type} block — set_hidden_field writes a hidden_field`,
          )
        }
        break
      }
      case 'disqualify':
        break
    }
  })

  // Extraction reads a text block that was actually put in ai_extraction mode.
  funnel.rubric.extraction.forEach((field, i) => {
    const block = blocksById.get(field.block_id)
    if (block === undefined) {
      issue(['rubric', 'extraction', i, 'block_id'], `no block with id "${field.block_id}"`)
      return
    }
    if (block.type !== 'short_text' && block.type !== 'long_text') {
      issue(
        ['rubric', 'extraction', i, 'block_id'],
        `"${block.id}" is a ${block.type} block — extraction reads free text`,
      )
      return
    }
    if (block.free_text_mode !== 'ai_extraction') {
      issue(
        ['rubric', 'extraction', i, 'block_id'],
        `"${block.id}" is in ${block.free_text_mode} mode — set its free_text_mode to ai_extraction, or drop this extraction field (PRD §3.6)`,
      )
    }
  })

  // Criteria: an answer_ref points at a real block, with a points map that
  // block can actually produce.
  funnel.rubric.criteria.forEach((criterion, i) => {
    if (criterion.source !== 'answer_ref') return
    const block = blocksById.get(criterion.block_id)
    if (block === undefined) {
      issue(['rubric', 'criteria', i, 'block_id'], `no block with id "${criterion.block_id}"`)
      return
    }
    if (!(QUESTION_BLOCK_TYPES as readonly string[]).includes(block.type)) {
      issue(
        ['rubric', 'criteria', i, 'block_id'],
        `"${block.id}" is a ${block.type} block — it produces no answer to score`,
      )
      return
    }
    const path: (string | number)[] = ['rubric', 'criteria', i, 'points']
    const map = criterion.points
    if (map.kind === 'options') {
      if (block.type === 'card_select' || block.type === 'dropdown') {
        const optionIds = new Set(block.options.map((o) => o.id))
        for (const key of Object.keys(map.points_by_option)) {
          if (!optionIds.has(key)) {
            issue(path, `"${block.id}" has no option "${key}" — score an option it offers`)
          }
        }
      } else if (block.type === 'consent') {
        for (const key of Object.keys(map.points_by_option)) {
          if (key !== 'true' && key !== 'false') {
            issue(path, `a consent block answers true or false, not "${key}"`)
          }
        }
      } else {
        issue(path, `an options map needs a card_select, dropdown or consent block, not ${block.type}`)
      }
    }
    if (map.kind === 'ranges' && !(NUMERIC_BLOCK_TYPES as readonly string[]).includes(block.type)) {
      issue(path, `a ranges map needs a number, currency or scale block, not ${block.type}`)
    }
    if (map.kind === 'keywords' && !(TEXT_BLOCK_TYPES as readonly string[]).includes(block.type)) {
      issue(path, `a keywords map needs a short_text or long_text block, not ${block.type}`)
    }
  })

  funnel.rubric.disqualifiers.forEach((disqualifier, i) => {
    checkCondition(disqualifier.when, ['rubric', 'disqualifiers', i, 'when'])
  })

  // Bands are the join key for outcomes, routing and voice personas.
  const bandIds = new Set(funnel.rubric.bands.map((b) => b.id))
  const seenOutcome = new Set<string>()
  funnel.outcomes.forEach((outcome, i) => {
    if (!bandIds.has(outcome.band_id)) {
      issue(['outcomes', i, 'band_id'], `no band with id "${outcome.band_id}"`)
    } else if (seenOutcome.has(outcome.band_id)) {
      issue(['outcomes', i, 'band_id'], `band "${outcome.band_id}" already has an outcome`)
    }
    seenOutcome.add(outcome.band_id)
  })
  funnel.routing.forEach((rule, i) => {
    if (!bandIds.has(rule.band_id)) {
      issue(['routing', i, 'band_id'], `no band with id "${rule.band_id}"`)
    }
  })
  funnel.voice.personas.forEach((persona, i) => {
    if (!bandIds.has(persona.band_id)) {
      issue(['voice', 'personas', i, 'band_id'], `no band with id "${persona.band_id}"`)
    }
  })

  // Story 2.1 AC: "a funnel serialising above 512 KB is rejected with a named
  // error", and an oversized or deeply-nested funnel is rejected without a
  // crash. JSON.stringify is the thing that can throw on pathological input,
  // so it is the thing that is caught.
  const bytes = serialisedBytes(funnel)
  if (bytes === null) {
    issue([], 'funnel_not_serialisable: this funnel cannot be serialised to JSON — a value in it is circular or nests too deeply')
  } else if (bytes > MAX_FUNNEL_BYTES) {
    issue(
      [],
      `funnel_too_large: this funnel serialises to ${Math.round(bytes / 1024)} KB and the ceiling is ${MAX_FUNNEL_BYTES / 1024} KB — split it, or shorten its longest content blocks (PRD §3.2, D1's 2 MB row cap)`,
    )
  }
}

/** UTF-8 byte length of the funnel as stored, or `null` if it cannot be
 *  serialised at all. `TextEncoder` is a web global, not a Node built-in —
 *  ARCH §2.3 forbids the latter in this package. */
export function serialisedBytes(value: unknown): number | null {
  try {
    return new TextEncoder().encode(JSON.stringify(value)).length
  } catch {
    return null
  }
}

/* ── the parse entry point ──────────────────────────────────────────────── */

export type FunnelIssue = { path: string; message: string }

export type ParseResult =
  | { ok: true; funnel: Funnel }
  | { ok: false; issues: FunnelIssue[] }

/**
 * Validate a funnel, reporting the failing PATH rather than a boolean.
 *
 * ARCH §4.4 returns these as the `details` of a 422; the builder puts each one
 * on the field it names. An error that says only "invalid" makes a 400-block
 * funnel unfixable, which is why the path is the contract and not a nicety.
 */
export function parseFunnel(input: unknown): ParseResult {
  const result = funnelSchema.safeParse(input)
  if (result.success) return { ok: true, funnel: result.data }
  return {
    ok: false,
    issues: result.error.issues.map((issue) => ({
      path: issue.path.join('.'),
      message: issue.message,
    })),
  }
}
